import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, CreditCard, Loader2, CheckCircle, XCircle, LogOut, ArrowLeft } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { apiFetch } from '../lib/api';

const Pricing = ({ currentUser: propUser, onLogout }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Post-payment account creation
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paidSessionId, setPaidSessionId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);

  useEffect(() => {
    const user = propUser || JSON.parse(localStorage.getItem('currentUser') || 'null');
    setCurrentUser(user);

    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (sessionId && success) {
      pollPaymentStatus(sessionId);
    } else if (canceled) {
      setMessage({ type: 'error', text: 'Paiement annulé.' });
    }
  }, [searchParams, propUser]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      setMessage({ type: 'error', text: 'Vérification du paiement expirée. Veuillez rafraîchir la page.' });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setMessage({ type: 'info', text: 'Vérification du paiement en cours...' });

    try {
      const response = await apiFetch(`/api/subscription/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setIsLoading(false);
        setPaidSessionId(sessionId);
        window.history.replaceState({}, document.title, '/pricing');

        // If user is already logged in, just activate subscription and redirect
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
        if (user?.id) {
          user.subscription_status = 'active';
          localStorage.setItem('currentUser', JSON.stringify(user));
          setMessage({ type: 'success', text: 'Abonnement activé !' });
          setPaymentSuccess(true);
          return;
        }

        // No user logged in: show account creation form
        setPaymentSuccess(true);
        setMessage({ type: 'success', text: 'Paiement réussi ! Créez votre compte pour accéder à CREA FLOW.' });
        return;

      } else if (data.status === 'expired') {
        setMessage({ type: 'error', text: 'Session de paiement expirée.' });
        setIsLoading(false);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la vérification du paiement.' });
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await apiFetch('/api/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({
          user_id: currentUser?.id || null,
          origin_url: window.location.origin
        })
      });
      const data = await response.json();

      if (data.error) {
        setMessage({ type: 'error', text: data.error });
        setIsLoading(false);
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        setMessage({ type: 'error', text: data.detail || 'Erreur lors de la création de la session de paiement.' });
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Subscription checkout error:', error);
      setMessage({ type: 'error', text: 'Impossible de contacter le serveur. Vérifiez votre connexion.' });
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères.' });
      return;
    }
    setCreatingAccount(true);

    try {
      const response = await apiFetch('/api/auth/create-from-payment', {
        method: 'POST',
        body: JSON.stringify({
          session_id: paidSessionId,
          password: newPassword,
          name: newName
        })
      });
      const data = await response.json();

      if (response.ok && data.user) {
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        setMessage({ type: 'success', text: 'Compte créé ! Redirection...' });
        setTimeout(() => { window.location.href = '/'; }, 1500);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Erreur lors de la création du compte.' });
        setCreatingAccount(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion.' });
      setCreatingAccount(false);
    }
  };

  const features = [
    'Gestion illimitée de projets',
    'Suivi des phases en temps réel',
    'Actions automatiques',
    'Gestion d\'équipe complète',
    'Documents et fichiers',
    'Insights et analytics',
    'Support prioritaire'
  ];

  const isLoggedIn = !!currentUser;
  const alreadyHasAccess = isLoggedIn && (currentUser.subscription_bypass || currentUser.subscription_status === 'active' || currentUser.subscription_status === 'trialing');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      {/* Nav */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="font-bold text-xl tracking-tight">CREA FLOW</span>
          </button>
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <span className="text-sm text-slate-500 hidden sm:inline">{currentUser.email}</span>
                {onLogout && (
                  <Button variant="ghost" size="sm" onClick={onLogout} data-testid="pricing-logout-btn">
                    <LogOut className="w-4 h-4 mr-1" />
                    Déconnexion
                  </Button>
                )}
              </>
            ) : (
              <Button variant="ghost" onClick={() => navigate('/')} data-testid="pricing-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Accueil
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-6 py-16 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Abonnement</h1>
          <p className="text-base text-slate-600 mt-2">
            Accédez à toutes les fonctionnalités de CREA FLOW
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-700 border border-green-200' :
            message.type === 'error' ? 'bg-red-100 text-red-700 border border-red-200' :
            'bg-blue-100 text-blue-700 border border-blue-200'
          }`} data-testid="pricing-message">
            {message.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0" />}
            {message.type === 'error' && <XCircle className="w-5 h-5 flex-shrink-0" />}
            {message.type === 'info' && <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />}
            {message.text}
          </div>
        )}

        {/* Already has access */}
        {alreadyHasAccess && (
          <Card className="p-6 bg-green-50 border-green-200 text-center">
            <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <p className="font-semibold text-green-700 mb-4">Votre accès est actif.</p>
            <Button className="w-full h-12 text-base" onClick={() => navigate('/')} data-testid="go-to-dashboard-btn">
              Accéder au tableau de bord
            </Button>
          </Card>
        )}

        {/* Post-payment: logged in user → direct to dashboard */}
        {paymentSuccess && isLoggedIn && !alreadyHasAccess && (
          <Card className="p-6 bg-green-50 border-green-200 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-green-800 mb-2">Bienvenue dans CREA FLOW !</h2>
            <p className="text-green-700 mb-4">Votre abonnement est actif.</p>
            <Button className="w-full h-12 text-base" onClick={() => window.location.href = '/'} data-testid="go-to-dashboard-after-payment-btn">
              Accéder au tableau de bord
            </Button>
          </Card>
        )}

        {/* Post-payment: no account → create account form */}
        {paymentSuccess && !isLoggedIn && (
          <Card className="p-8 bg-white" data-testid="create-account-card">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">Créez votre compte</h2>
            <p className="text-slate-500 text-center mb-6">Votre paiement est confirmé. Définissez vos identifiants pour accéder à CREA FLOW.</p>
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom (optionnel)</label>
                <Input
                  type="text"
                  placeholder="Votre nom"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="create-account-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe *</label>
                <Input
                  type="password"
                  placeholder="Minimum 6 caractères"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="create-account-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={creatingAccount}
                data-testid="create-account-submit"
              >
                {creatingAccount ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création...</>
                ) : (
                  'Créer mon compte et accéder au dashboard'
                )}
              </Button>
            </form>
          </Card>
        )}

        {/* Pricing Card: shown when no payment success yet and no existing access */}
        {!paymentSuccess && !alreadyHasAccess && (
          <Card className="p-8 bg-white" data-testid="pricing-card">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">CREA FLOW Pro</h2>
              <p className="text-slate-500">Pour tous les professionnels</p>
            </div>
            <div className="text-center mb-6">
              <span className="text-5xl font-bold text-slate-900">9,99€</span>
              <span className="text-slate-500"> /mois</span>
            </div>
            <ul className="space-y-3 mb-8">
              {features.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{f}</span>
                </li>
              ))}
            </ul>
            <Button
              className="w-full h-12 text-base"
              onClick={handleSubscribe}
              disabled={isLoading}
              data-testid="subscribe-btn"
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Chargement...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> S'abonner - 9,99€/mois</>
              )}
            </Button>
          </Card>
        )}

        <p className="text-center text-sm text-slate-500">
          Paiement sécurisé par Stripe. Annulez à tout moment.
        </p>
      </div>
    </div>
  );
};

export default Pricing;
